import type { CellType, ColumnSchema, WorkerSchema, WorkspaceConfig } from '../types'
import { cn } from '../utils/cn'
import { columnWidthPercents, resolveColumnWidth } from '../utils/columnSizing'
import { AeraButton } from '@aera/ui'

const ROW_COUNT = 12

const FALLBACK_COLUMNS: ColumnSchema[] = [
  {
    id: 'date',
    header: 'วันที่',
    type: 'datetime',
    editable: false,
    visibility: 'always',
    width: 150,
  },
  {
    id: 'amount',
    header: 'ยอด',
    type: 'amount',
    editable: false,
    visibility: 'always',
    align: 'right',
    width: 200,
  },
  {
    id: 'status',
    header: 'สถานะ',
    type: 'status',
    editable: false,
    visibility: 'always',
    width: 140,
  },
  {
    id: 'name',
    header: 'ชื่อ',
    type: 'text',
    editable: false,
    visibility: 'sm',
    width: 200,
  },
  {
    id: 'bank',
    header: 'ธนาคาร',
    type: 'bank',
    editable: false,
    visibility: 'sm',
    width: 200,
  },
]

const RECEIVABLE_WORKER_IDS = new Set([
  'bill-lin-receivable',
  'bill-lin-receivable-slip',
  'bill-lin-receivable-address',
])

const RECEIVABLE_TABLE_VISIBILITIES = new Set<ColumnSchema['visibility']>([
  'always',
  'sm',
])

const TEXT_WIDTHS = ['w-24', 'w-20', 'w-28', 'w-16'] as const

function SkeletonBlock({ className }: { className: string }) {
  return (
    <div
      className={cn(
        'shrink-0 animate-pulse rounded-md bg-aera-lightgray/80',
        className,
      )}
      aria-hidden
    />
  )
}

function skeletonHeaderBlock(column: ColumnSchema, columnIndex: number) {
  if (column.type === 'amount') {
    return <SkeletonBlock className="h-3 w-20" />
  }
  if (column.type === 'status' || column.id === '__status__') {
    return <SkeletonBlock className="h-4 w-16 rounded-md" />
  }
  if (column.type === 'datetime' || column.type === 'date') {
    return <SkeletonBlock className="h-3 w-24" />
  }
  if (column.type === 'bank' || column.type === 'image') {
    return <SkeletonBlock className="h-4 w-12 rounded-md" />
  }

  return (
    <SkeletonBlock
      className={cn(
        'h-3',
        columnIndex % 3 === 0 ? 'w-20' : columnIndex % 3 === 1 ? 'w-16' : 'w-14',
      )}
    />
  )
}

function columnPassesWorkspaceGate(
  column: ColumnSchema,
  workspace?: WorkspaceConfig,
) {
  if (column.visibility === 'hidden') return false
  if (column.configGate?.length && workspace) {
    if (!column.configGate.includes(workspace.bill_lin_config)) return false
  }
  if (column.scaleGate && workspace) {
    if (column.scaleGate !== workspace.scale) return false
  }
  return true
}

function syntheticColumn(
  id: string,
  header: string,
  type: CellType,
  width: number,
): ColumnSchema {
  return {
    id,
    header,
    type,
    editable: false,
    visibility: 'always',
    width,
  }
}

function resolveSkeletonColumns(
  schema?: WorkerSchema,
  workspace?: WorkspaceConfig,
): ColumnSchema[] {
  if (!schema) return FALLBACK_COLUMNS

  const available = schema.columns.filter((column) =>
    columnPassesWorkspaceGate(column, workspace),
  )

  if (RECEIVABLE_WORKER_IDS.has(schema.workerId)) {
    const dataColumns = available.filter(
      (column) =>
        RECEIVABLE_TABLE_VISIBILITIES.has(column.visibility) &&
        column.id !== '__status__' &&
        column.type !== 'status',
    )

    const statusWidth = schema.workerId === 'bill-lin-receivable-slip' ? 200 : 140

    return [
      ...dataColumns.slice(0, 6),
      syntheticColumn('__status__', 'สถานะ', 'status', statusWidth),
    ]
  }

  return [...available]
    .filter((column) => column.visibility === 'always')
    .sort(
      (left, right) =>
        visibilitySortRank(left.visibility) -
        visibilitySortRank(right.visibility),
    )
}

const VISIBILITY_SORT_RANK: Record<ColumnSchema['visibility'], number> = {
  always: 0,
  sm: 1,
  md: 2,
  lg: 3,
  hidden: 99,
}

function visibilitySortRank(visibility: ColumnSchema['visibility']) {
  return VISIBILITY_SORT_RANK[visibility]
}

function columnWidth(column: ColumnSchema) {
  return resolveColumnWidth(column)
}

function SkeletonCellContent({
  column,
  rowIndex,
  columnIndex,
}: {
  column: ColumnSchema
  rowIndex: number
  columnIndex: number
}) {
  if (column.id === '__kind__') {
    return <SkeletonBlock className="h-5 w-11 rounded-md" />
  }

  switch (column.type) {
    case 'amount':
      return <SkeletonBlock className="h-3.5 w-20" />
    case 'status':
      return <SkeletonBlock className="h-5 w-20 rounded-md" />
    case 'bank':
      return <SkeletonBlock className="h-5 w-12 rounded-md" />
    case 'image':
      return <SkeletonBlock className="h-6 w-6 rounded-md" />
    case 'datetime':
    case 'date':
      return <SkeletonBlock className="h-3.5 w-24" />
    default: {
      const width =
        TEXT_WIDTHS[(rowIndex + columnIndex) % TEXT_WIDTHS.length] ?? 'w-20'
      return <SkeletonBlock className={cn('h-3.5', width)} />
    }
  }
}

const TABLE_HEAD_CLASS =
  'h-10 px-3 text-[11px] font-bold text-aera-lightslate uppercase tracking-wider align-middle whitespace-nowrap'
const TABLE_CELL_CLASS =
  'h-[36.5px] max-h-[36.5px] overflow-hidden px-3 py-0 align-middle'

/** Bottom pager placeholders — same chrome + button size as loaded footer. */
export function DataTablePaginationSkeleton() {
  return (
    <div
      className="shrink-0 rounded-b-2xl border-t border-aera-lightgray bg-aera-white backdrop-blur-md"
      aria-hidden
    >
      <div className="flex items-center justify-between gap-3 px-3 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-aera-gray md:px-4">
        <SkeletonBlock className="h-3 w-44" />
        <div className="flex items-center gap-2">
          <span className="relative inline-flex">
            <AeraButton
              variant="outline"
              size="xs"
              fontWeight="bold"
              textSize="xs"
              disabled
              tabIndex={-1}
              className="pointer-events-none uppercase invisible"
            >
              ก่อนหน้า
            </AeraButton>
            <span className="absolute inset-0 animate-pulse rounded-xl bg-aera-lightgray/80" />
          </span>
          <span className="relative inline-flex">
            <AeraButton
              variant="outline"
              size="xs"
              fontWeight="bold"
              textSize="xs"
              disabled
              tabIndex={-1}
              className="pointer-events-none uppercase invisible"
            >
              ถัดไป
            </AeraButton>
            <span className="absolute inset-0 animate-pulse rounded-xl bg-aera-lightgray/80" />
          </span>
        </div>
      </div>
    </div>
  )
}

export function DataTableSkeleton({
  rows = ROW_COUNT,
  className,
  schema,
  workspaceConfig,
}: {
  rows?: number
  className?: string
  schema?: WorkerSchema
  workspaceConfig?: WorkspaceConfig
}) {
  const columns = resolveSkeletonColumns(schema, workspaceConfig)
  const columnSizes = columns.map((column) => columnWidth(column))
  const totalColumnWidth = columnSizes.reduce((total, width) => total + width, 0)
  const minWidth = Math.max(totalColumnWidth, 720)
  const colPercents = columnWidthPercents(columnSizes)

  return (
    <div
      className={cn(
        'relative min-h-0 flex-1 overflow-x-auto overflow-y-auto custom-scrollbar bg-aera-white',
        className,
      )}
      aria-busy="true"
      aria-label="กำลังโหลดตาราง"
    >
      <table
        className="w-full border-collapse table-fixed"
        style={{ minWidth: `${minWidth}px` }}
      >
        <colgroup>
          {columns.map((column, index) => (
            <col
              key={column.id}
              style={{
                width: `${colPercents[index]}%`,
              }}
            />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-5">
          <tr className="bg-aera-white/90 backdrop-blur-md border-b border-aera-lightgray">
            {columns.map((column, columnIndex) => {
              const alignRight =
                column.align === 'right' || column.type === 'amount'
              return (
              <th
                key={column.id}
                className={cn(
                  TABLE_HEAD_CLASS,
                  alignRight ? 'text-right' : 'text-left',
                )}
              >
                <div
                  className={cn(
                    'flex h-10 items-center',
                    alignRight ? 'justify-end' : 'justify-start',
                  )}
                >
                  {skeletonHeaderBlock(column, columnIndex)}
                </div>
              </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr
              key={rowIndex}
              className="h-[36.5px] border-b border-aera-lightgray last:border-b-0 pointer-events-none"
            >
              {columns.map((column, columnIndex) => {
                const alignRight =
                  column.align === 'right' || column.type === 'amount'
                return (
                <td key={column.id} className={TABLE_CELL_CLASS}>
                  <div
                    className={cn(
                      'flex h-full max-h-[36.5px] w-full min-w-0 items-center overflow-hidden',
                      alignRight ? 'justify-end' : 'justify-start',
                    )}
                  >
                    <SkeletonCellContent
                      column={column}
                      rowIndex={rowIndex}
                      columnIndex={columnIndex}
                    />
                  </div>
                </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
